import type { Dispatch, ReducerState, ReducerWithoutAction } from "react";
import { useEffect, useReducer } from "react";

import { useQuery } from "@tanstack/react-query";
import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";

import type { IChosenItems } from "app/@types/chosenItems";
import type { TranslateFunction } from "app/@types/I18nInterfaces";
import type { IncludeExcludeKeywords } from "app/pages/digital-marketing/brand-monitoring-campaigns/shared/useBPRequestBody";
import type {
    RankingDistributionCompareModeData,
    RankingDistributionPayload,
} from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/rankingDistribution.types";
import {
    Position,
    RankingDistributionMetric,
} from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/rankingDistribution.types";
import { RankingDistributionChart } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/RankingDistributionChart";
import { RankingDistributionCompareModePanel } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/RankingDistributionCompareModePanel";
import { RankingDistributionLegends } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/RankingDIstributionLegends";
import { RankingDistributionTableTitleContainer } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/RankingDistributionTableTitleContainer";
import { RANKING_DISTRIBUTION_DICTIONARY } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/RankingDistributionTranslator";
import {
    RankingDistributionCompareModeBodyWrapper,
    RankingDistributionCompareModeContainer,
    TableTopContainer,
} from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/StyledComponents";
import { useRankingDistributionTableTopContext } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/useRankingDistributionTableTopContext";
import { MAP_METRIC_TO_FORMATTING_FUNCTION } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/Utilities";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import DurationService from "app/services/DurationService";
import { DefaultFetchService, requestParams } from "app/services/fetchService";
import { GRAPH_API } from "./Constants";
import type { IButtonProps } from "./RankingDistributionTableTop";
import { allTrackers } from "app/services/track/track";

const HorizontalLineWrapper = styled.div`
    background: ${colorsPalettes.carbon[0]};
`;

const HorizontalLine = styled.div`
    height: 1px;
    background: #cbd1d7;
    margin-bottom: 8px;
    margin-top: 11px;
`;

type RankingDistributionState = {
    rankingDistributionByPositionData: RankingDistributionCompareModeData;
    rankingDistributionByWebsiteData: RankingDistributionCompareModeData;
    currentMetric?: RankingDistributionMetric;
    totalKeywordsPerPosition: { key: Position; y: number }[];
};

const RankingDistributionInitialState: RankingDistributionState = {
    rankingDistributionByPositionData: undefined,
    rankingDistributionByWebsiteData: undefined,
    totalKeywordsPerPosition: [],
};

enum RankingDistributionReducerTypes {
    UpdateRankingDistributionData = "UPDATE_RANKING_DISTRIBUTION_DATA",
    ToggleActivenessOfGivenEntity = "TOGGLE_ACTIVENESS_OF_GIVEN_ENTITY",
    OnMetricChange = "SET_ALL_ELEMENTS_ACTIVE_AND_SET_NEW_METRIC",
    fetchData = "FETCH_DATA",
}

type RankingDistributionDataActions =
    | {
          type: RankingDistributionReducerTypes.UpdateRankingDistributionData;
          payload: {
              dataPayload: RankingDistributionPayload;
              translate: TranslateFunction;
              chosenItems: IChosenItems;
              metric: string;
              pageFilter: string;
          };
      }
    | {
          type: RankingDistributionReducerTypes.ToggleActivenessOfGivenEntity;
          payload: { entityName: string };
      }
    | {
          type: RankingDistributionReducerTypes.OnMetricChange;
          payload: { newMetric: RankingDistributionMetric };
      }
    | { type: RankingDistributionReducerTypes.fetchData };

const MAP_METRIC_TO_DATA_NAME: Record<RankingDistributionMetric, string> = {
    [RankingDistributionMetric.ByPosition]: "rankingDistributionByPositionData",
    [RankingDistributionMetric.ByWebsite]: "rankingDistributionByWebsiteData",
};

const rankingDistributionDataReducer = (state: RankingDistributionState, action: RankingDistributionDataActions) => {
    switch (action.type) {
        case RankingDistributionReducerTypes.fetchData: {
            return {
                ...state,
            };
        }
        case RankingDistributionReducerTypes.UpdateRankingDistributionData:
            const { metric, pageFilter } = action.payload;
            return {
                ...state,
                rankingDistributionByPositionData: MAP_METRIC_TO_FORMATTING_FUNCTION[
                    RankingDistributionMetric.ByPosition
                ](action.payload.dataPayload, { chosenItems: action.payload.chosenItems }, pageFilter),
                rankingDistributionByWebsiteData: MAP_METRIC_TO_FORMATTING_FUNCTION[
                    RankingDistributionMetric.ByWebsite
                ](action.payload.dataPayload, { translate: action.payload.translate }, pageFilter),
                currentMetric: metric
                    ? Number(RankingDistributionMetric[metric])
                    : RankingDistributionMetric.ByPosition,
            };
        case RankingDistributionReducerTypes.ToggleActivenessOfGivenEntity:
            const { entityName } = action.payload;
            const dataToChange = MAP_METRIC_TO_DATA_NAME[state.currentMetric];
            return {
                ...state,
                [dataToChange]: state[dataToChange].map((dataUnit) => {
                    return {
                        ...dataUnit,
                        isActive: entityName === dataUnit.name ? !dataUnit.isActive : dataUnit.isActive,
                    };
                }),
            };
    }
};

type RankingDistributionCompareModeState = typeof rankingDistributionDataReducer;

type RankingDistributionCompareModeProps = {
    dataParamsAdapter: (params: Record<string, unknown>) => Record<string, unknown>;
    metricTitle?: string;
    buttonsProps: IButtonProps;
    metric: string;
    requestBody?: IncludeExcludeKeywords[];
    pageFilter?: string;
};

export const RankingDistributionCompareMode = ({
    dataParamsAdapter,
    metricTitle,
    buttonsProps,
    metric,
    requestBody,
    pageFilter,
}: RankingDistributionCompareModeProps) => {
    const [state, dispatch]: [
        ReducerState<RankingDistributionCompareModeState>,
        Dispatch<RankingDistributionDataActions>,
    ] = useReducer(
        rankingDistributionDataReducer as ReducerWithoutAction<RankingDistributionState>,
        undefined,
        () => RankingDistributionInitialState,
    );

    const { rankingDistributionByPositionData, rankingDistributionByWebsiteData, currentMetric } = state;

    const translate = useTranslation();

    const { tableFilters, chosenItems } = useRankingDistributionTableTopContext();
    const { duration, webSource, country, ranking } = tableFilters;

    const durationData = DurationService.getDurationData(duration);

    const params = dataParamsAdapter({ ...tableFilters });

    const paramsAsString = requestParams({ ...params, currentMetric: RankingDistributionMetric[metric] });

    const { data, isLoading, isError } = useQuery<RankingDistributionPayload>({
        queryKey: [GRAPH_API, duration, webSource, country, ranking, tableFilters, metric],
        queryFn: async () => {
            dispatch({
                type: RankingDistributionReducerTypes.fetchData,
            });
            const response = await DefaultFetchService.getInstance().post<RankingDistributionPayload | undefined>(
                `${GRAPH_API}?${paramsAsString}`,
                requestBody,
            );

            let transformedData = response;
            if (response && typeof response === "object") {
                transformedData = {
                    ...transformedData,
                    [Position.GreaterThanTwenty]: transformedData[Position.TwentyOneToFifty] || {},
                };
                delete transformedData[Position.TwentyOneToFifty];
            }

            return transformedData;
        },
    });

    useEffect(() => {
        if (data) {
            dispatch({
                type: RankingDistributionReducerTypes.UpdateRankingDistributionData,
                payload: {
                    dataPayload: data,
                    translate,
                    chosenItems,
                    metric,
                    pageFilter,
                },
            });
        }
    }, [data, dispatch, translate, chosenItems, metric, pageFilter]);

    const getCurrentRankingDistributionData: () => RankingDistributionCompareModeData = () => {
        return currentMetric === RankingDistributionMetric.ByWebsite
            ? rankingDistributionByWebsiteData
            : rankingDistributionByPositionData;
    };

    const filterActiveRankingDistributionDataItems: () => RankingDistributionCompareModeData = () => {
        const currentData = getCurrentRankingDistributionData();
        return currentData ? currentData.filter(({ isActive }) => isActive) : [];
    };

    const rankingDistributionFormattedData = getCurrentRankingDistributionData();
    const onMetricChange = (newMetric: RankingDistributionMetric) => {
        const metricLabel = newMetric === RankingDistributionMetric.ByPosition ? "by position" : "by website";
        allTrackers.dispatchTrackEvent("Tab", "switch", metricLabel);
        void proNavigator.updateParams({
            metric: RankingDistributionMetric[newMetric],
        });
    };

    const toggleActivenessOfGivenEntity = (entityName: string) => {
        dispatch({
            type: RankingDistributionReducerTypes.ToggleActivenessOfGivenEntity,
            payload: { entityName },
        });
    };

    const noData = isError
        ? {
              title: translate(RANKING_DISTRIBUTION_DICTIONARY.ERROR_TITLE),
              subtitle: translate(RANKING_DISTRIBUTION_DICTIONARY.ERROR_SUBTITLE),
          }
        : {
              title: translate(RANKING_DISTRIBUTION_DICTIONARY.NO_DATA_TITLE),
              subtitle: translate(RANKING_DISTRIBUTION_DICTIONARY.NO_DATA_SUBTITLE),
          };

    const hasData =
        !isLoading && !isError && Object.values(data).some((value) => Object.values(value).some((v) => v.length > 0));
    const dataToDisplay = filterActiveRankingDistributionDataItems();

    return (
        <RankingDistributionCompareModeContainer>
            <RankingDistributionCompareModePanel
                metricTitle={metricTitle}
                metric={currentMetric}
                setCurrentMetric={onMetricChange}
                switchTitles={[
                    translate(RANKING_DISTRIBUTION_DICTIONARY.PRIMARY_METRIC),
                    translate(RANKING_DISTRIBUTION_DICTIONARY.SECONDARY_METRIC),
                ]}
            />
            <HorizontalLineWrapper>
                <HorizontalLine />
            </HorizontalLineWrapper>
            <RankingDistributionCompareModeBodyWrapper>
                <RankingDistributionChart
                    isLoading={isLoading}
                    data={dataToDisplay}
                    metric={currentMetric}
                    hasData={hasData}
                    translatedWords={{
                        total: translate(RANKING_DISTRIBUTION_DICTIONARY.TOTAL),
                        positions: translate(RANKING_DISTRIBUTION_DICTIONARY.POSITIONS),
                        website: translate(RANKING_DISTRIBUTION_DICTIONARY.WEBSITE),
                        domain: translate(RANKING_DISTRIBUTION_DICTIONARY.DOMAIN),
                        change: translate(RANKING_DISTRIBUTION_DICTIONARY.CHANGE),
                        keywords: translate(RANKING_DISTRIBUTION_DICTIONARY.KEYWORDS),
                        totalKeywords: translate(RANKING_DISTRIBUTION_DICTIONARY.TOTAL_KEYWORDS),
                        ofSiteKeywords: translate(RANKING_DISTRIBUTION_DICTIONARY.OF_SITE_KEYWORDS),
                        noDataTitle: noData.title,
                        noDataSubtitle: noData.subtitle,
                    }}
                    tooltipSubtitle={durationData.forWidget as string}
                />
                <RankingDistributionLegends
                    data={rankingDistributionFormattedData}
                    onLegendClick={toggleActivenessOfGivenEntity}
                    currentMetric={currentMetric}
                    legendsTableTitles={[
                        translate(
                            RANKING_DISTRIBUTION_DICTIONARY.RANKING_DISTRIBUTION_PRIMARY_METRIC_FIRST_TABLE_TITLE,
                        ),
                        translate(
                            RANKING_DISTRIBUTION_DICTIONARY.RANKING_DISTRIBUTION_PRIMARY_METRIC_SECOND_TABLE_TITLE,
                        ),
                    ]}
                />
            </RankingDistributionCompareModeBodyWrapper>
            <TableTopContainer>
                <RankingDistributionTableTitleContainer
                    {...buttonsProps}
                    requestBody={requestBody}
                    duration={duration}
                />
            </TableTopContainer>
        </RankingDistributionCompareModeContainer>
    );
};
